import { useCallback } from 'react';

import { VALID_DATE_FORMATS } from '@adsk/alloy-react-datetime';
import {
  type BackwardsCompatibleMutableRefObject,
  noop,
} from '@adsk/alloy-react-helpers';

import useDateSelection from './useDateSelection';
import useKeyDownHandler from './useKeyDownHandler';
import useSharedDateLogic from './useSharedDateLogic';
import { DATE_PICKER_VARIANTS } from '../consts';
import { getFieldValue, normalizeDate } from '../helpers';
import type { UseDatePickerCommonArgs, DatePickerArgs } from '../types';

export type UseDatePickerProps = UseDatePickerCommonArgs &
  DatePickerArgs & {
    autoFocus?: boolean;
    target?: BackwardsCompatibleMutableRefObject<HTMLDivElement>;
    setShouldShowSelection: React.Dispatch<React.SetStateAction<boolean>>;
  };

function useDatePicker({
  /** core props */
  value: propsDate,
  onChange: propsOnChange = noop,
  initialValue = null,
  /** picker props */
  variant = DATE_PICKER_VARIANTS.FIELD,
  /** trigger props */
  error,
  format = VALID_DATE_FORMATS.NUMERIC,
  placeholder,
  triggerProps,
  setShouldShowSelection,
  autoFocus,
  ...props
}: UseDatePickerProps) {
  const {
    T,
    locale,
    minDate,
    maxDate,
    open,
    toggleOverlay,
    placement,
    arrowProps,
    targetProps,
    overlayProps,
    onFinalSelection,
    context,
  } = useSharedDateLogic({ variant, ...props });

  let controlledDate = propsDate;
  let initialDate = initialValue;

  if (props.timeZone) {
    if (controlledDate) {
      controlledDate = normalizeDate({
        date: controlledDate,
        timeZone: props.timeZone,
      });
    }

    if (initialDate) {
      initialDate = normalizeDate({
        date: initialDate,
        timeZone: props.timeZone,
      });
    }
  }

  const { date, onDateSelected, resetSelection } = useDateSelection({
    initialDate,
    propsDate: controlledDate,
    propsOnChange,
    onFinalSelection,
    setShouldShowSelection,
  });

  useKeyDownHandler({ open, resetSelection });

  const getTriggerProps = useCallback(() => {
    return variant === DATE_PICKER_VARIANTS.FIELD
      ? {
          autoFocus,
          disabled: props.disabled,
          error,
          open,
          variant,
          placeholder:
            placeholder?.toString() ?? T.translate('DD/MM/YYYY')?.toString(),
          value: getFieldValue({
            date,
            format,
            locale,
          }),
          ...triggerProps,
          'data-testid': 'DatePickerTarget',
        }
      : {
          autoFocus,
          variant,
          disabled: props.disabled,
          ...triggerProps,
          'data-testid': 'DatePickerTarget',
        };
  }, [
    variant,
    autoFocus,
    props.disabled,
    error,
    open,
    placeholder,
    T,
    date,
    format,
    locale,
    triggerProps,
  ]);

  return {
    minDate,
    maxDate,
    open,
    date,
    variant,
    toggleOverlay,
    getTriggerProps,
    onDateSelected,
    resetSelection,
    // floating-ui
    placement,
    arrowProps,
    targetProps,
    overlayContainerProps: overlayProps,
    context,
  };
}

export default useDatePicker;
